import { useEffect } from 'react';

/**
 * Overrides the document's <link rel="canonical"> href while the calling
 * component is mounted, restoring the previous value on unmount.
 *
 * Why this exists: the CRA shell ships a single static canonical — the
 * homepage — in public/index.html, and that same tag is served on every
 * SPA route. Any non-homepage page that should be indexed on its own
 * therefore tells Google "I'm just an alternate of the homepage," and
 * Search Console reports it under "Alternate page with proper canonical
 * tag" and folds it into the homepage instead of indexing it. Calling this
 * with the page's absolute canonical URL gives that route a self-referential
 * canonical so Google indexes it as a distinct page.
 *
 * Googlebot renders JS before indexing, so the swapped href is picked up.
 *
 * @param {string} href Absolute canonical URL, e.g. 'https://ehandshake.com/pricing'
 */
export default function useCanonical(href) {
  useEffect(() => {
    if (!href) return undefined;
    const link = document.querySelector('link[rel="canonical"]');
    if (!link) return undefined;

    const previous = link.getAttribute('href');
    link.setAttribute('href', href);

    // Restore the default (homepage) canonical when leaving the page so a
    // client-side navigation doesn't leave a stale canonical on the next route.
    return () => {
      if (previous) link.setAttribute('href', previous);
    };
  }, [href]);
}
